﻿/**
 * songPatternGenerator.ts
 *
 * TypeScript port of final_version.py — pure function module, no external dependencies.
 * Generates abstract song patterns (sequence of {section: bars} objects).
 *
 * Ticket 20260326e
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SectionName = 'intro' | 'verse' | 'chorus' | 'pre' | 'bridge' | 'ending';
export type PatternSlot = { [K in SectionName]?: number };
export type SongPattern = PatternSlot[];
export type Mode = 'standard' | 'creative' | 'crazy';

// ---------------------------------------------------------------------------
// Section bar ranges (creative / crazy modes)
// ---------------------------------------------------------------------------

export const SECTION_RANGES: Record<SectionName, number[]> = {
    intro:   [4, 8, 10, 12],
    verse:   [4, 6, 8, 10, 12, 16],
    chorus:  [4, 6, 8, 10, 12, 16],
    pre:     [2, 4, 6, 8],
    bridge:  [2, 4, 6, 8, 10, 12],
    ending:  [4, 8, 10, 12],
};

// ---------------------------------------------------------------------------
// Canonical standard patterns (standard mode)
// ---------------------------------------------------------------------------

type CanonicalVariant = [SectionName, number][];

export const CANONICAL_STANDARD: Record<string, CanonicalVariant[]> = {
    blues: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],
        [['intro',4],['verse',12],['verse',12],['verse',12],['ending',4]],
        [['intro',4],['verse',8],['verse',8],['ending',4]],
        [['intro',4],['verse',12],['chorus',12],['ending',4]],
        [['intro',4],['verse',12],['verse',12],['chorus',12],['ending',4]],
        [['intro',8],['verse',12],['verse',12],['chorus',12],['chorus',12],['ending',8]],
        [['intro',4],['verse',12],['verse',12],['bridge',12],['verse',12],['ending',4]],
        [['intro',12],['verse',12],['verse',12],['verse',12],['ending',12]],
        [['intro',4],['verse',12],['pre',4],['chorus',12],['verse',12],['ending',4]],
        [['intro',4],['verse',8],['verse',8],['verse',8],['verse',8],['ending',4]],
        [['intro',4],['verse',8],['chorus',8],['verse',8],['chorus',8],['ending',4]],
        [['intro',8],['verse',8],['verse',8],['bridge',8],['chorus',8],['ending',8]],
        [['intro',4],['verse',8],['verse',8],['ending',4]],
        [['intro',10],['verse',10],['verse',10],['ending',10]],
        [['intro',4],['verse',10],['chorus',10],['ending',4]],
    ],
    jazz: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',12],['verse',12],['verse',12],['ending',8]],
        [['intro',8],['verse',16],['chorus',16],['ending',8]],
        [['intro',4],['verse',16],['chorus',16],['ending',4]],
        [['intro',4],['verse',16],['verse',16],['ending',4]],
        [['intro',8],['verse',16],['bridge',8],['verse',8],['ending',8]],
        [['intro',12],['verse',16],['chorus',16],['ending',12]],
        [['intro',8],['verse',16],['chorus',16],['ending',8]],
        [['intro',4],['verse',16],['verse',16],['verse',16],['ending',4]],
        [['intro',8],['verse',16],['bridge',12],['verse',16],['ending',12]],
        [['intro',12],['verse',16],['chorus',16],['ending',12]],
        [['intro',10],['verse',10],['verse',10],['ending',10]],
        [['intro',4],['verse',10],['chorus',10],['ending',4]],
    ],
    rock: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',16],['chorus',8],['verse',16],['pre',4],['chorus',8],['bridge',8],['chorus',8],['ending',8]],
        [['intro',8],['verse',12],['chorus',12],['verse',12],['chorus',12],['bridge',8],['chorus',12],['ending',8]],
        [['intro',4],['verse',16],['pre',8],['chorus',8],['verse',16],['pre',8],['chorus',8],['ending',4]],
        [['intro',12],['verse',16],['chorus',8],['bridge',12],['chorus',8],['chorus',8],['ending',12]],
        [['intro',4],['verse',12],['verse',12],['bridge',8],['verse',12],['ending',8]],
        [['intro',10],['verse',10],['chorus',10],['ending',10]],
        [['intro',4],['verse',10],['bridge',10],['chorus',10],['ending',4]],
    ],
    country: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',16],['chorus',8],['verse',16],['pre',4],['chorus',8],['bridge',8],['chorus',8],['ending',8]],
        [['intro',8],['verse',12],['chorus',12],['verse',12],['chorus',12],['bridge',8],['chorus',12],['ending',8]],
        [['intro',4],['verse',16],['pre',8],['chorus',8],['verse',16],['pre',8],['chorus',8],['ending',4]],
        [['intro',12],['verse',16],['chorus',8],['bridge',12],['chorus',8],['chorus',8],['ending',12]],
        [['intro',4],['verse',12],['verse',12],['bridge',8],['verse',12],['ending',8]],
        [['intro',10],['verse',10],['chorus',10],['ending',10]],
        [['intro',4],['verse',10],['bridge',10],['chorus',10],['ending',4]],
    ],
    upbeat: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',4],['verse',8],['chorus',8],['verse',8],['pre',4],['chorus',8],['bridge',8],['chorus',8],['ending',4]],
        [['intro',4],['verse',8],['chorus',8],['verse',8],['chorus',8],['ending',4]],
        [['intro',8],['verse',8],['verse',8],['chorus',8],['verse',8],['ending',8]],
        [['intro',4],['verse',8],['pre',4],['chorus',8],['verse',8],['pre',4],['chorus',8],['ending',4]],
        [['intro',10],['verse',10],['verse',10],['ending',10]],
        [['intro',4],['verse',10],['chorus',10],['ending',4]],
    ],
    brushes: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',16],['chorus',16],['ending',8]],
        [['intro',4],['verse',16],['verse',16],['ending',4]],
        [['intro',8],['verse',16],['bridge',8],['verse',8],['ending',8]],
        [['intro',8],['verse',12],['verse',12],['verse',12],['ending',8]],
        [['intro',10],['verse',10],['verse',10],['ending',10]],
        [['intro',4],['verse',10],['chorus',10],['ending',4]],
    ],
    boggie: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',4],['verse',12],['verse',12],['verse',12],['ending',4]],
        [['intro',4],['verse',8],['chorus',8],['verse',8],['chorus',8],['ending',4]],
        [['intro',8],['verse',12],['verse',12],['bridge',8],['verse',12],['ending',8]],
        [['intro',4],['verse',12],['pre',4],['chorus',12],['verse',12],['ending',4]],
        [['intro',4],['verse',10],['verse',10],['ending',4]],
        [['intro',10],['verse',10],['chorus',10],['ending',10]],
    ],
    garage: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',4],['verse',8],['chorus',8],['verse',8],['pre',4],['chorus',8],['bridge',8],['chorus',8],['ending',4]],
        [['intro',4],['verse',12],['verse',12],['bridge',8],['verse',12],['ending',4]],
        [['intro',8],['verse',8],['verse',8],['chorus',8],['verse',8],['ending',8]],
        [['intro',12],['verse',16],['pre',4],['chorus',8],['bridge',8],['ending',12]],
        [['intro',4],['verse',10],['chorus',10],['ending',4]],
        [['intro',10],['verse',10],['bridge',10],['ending',10]],
    ],
    pop: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',16],['chorus',8],['verse',16],['chorus',8],['bridge',8],['chorus',8],['ending',8]],
        [['intro',4],['verse',16],['pre',8],['chorus',16],['verse',16],['pre',8],['chorus',16],['ending',8]],
        [['intro',4],['verse',8],['chorus',8],['verse',8],['chorus',8],['bridge',4],['chorus',8],['ending',4]],
        [['intro',12],['verse',16],['pre',4],['chorus',8],['bridge',12],['chorus',8],['ending',12]],
        [['intro',4],['verse',10],['chorus',10],['ending',4]],
        [['intro',10],['verse',10],['bridge',10],['chorus',10],['ending',10]],
    ],
    funk: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',16],['chorus',8],['bridge',8],['verse',16],['chorus',8],['ending',8]],
        [['intro',12],['verse',16],['verse',16],['chorus',8],['bridge',12],['ending',12]],
        [['intro',4],['verse',8],['bridge',8],['verse',8],['bridge',8],['chorus',8],['ending',4]],
        [['intro',8],['verse',12],['pre',4],['chorus',16],['verse',12],['pre',4],['chorus',16],['ending',8]],
        [['intro',10],['verse',10],['chorus',10],['ending',10]],
        [['intro',4],['verse',10],['bridge',10],['chorus',10],['ending',4]],
    ],
    reggae: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',16],['chorus',8],['verse',16],['chorus',8],['pre',4],['chorus',8],['ending',8]],
        [['intro',8],['verse',8],['chorus',8],['verse',8],['chorus',8],['ending',8]],
        [['intro',4],['verse',12],['pre',4],['chorus',8],['verse',12],['ending',4]],
        [['intro',8],['verse',16],['chorus',16],['bridge',8],['chorus',16],['ending',12]],
        [['intro',12],['verse',16],['verse',16],['chorus',16],['ending',12]],
        [['intro',4],['verse',8],['chorus',8],['verse',8],['chorus',8],['bridge',8],['ending',4]],
        [['intro',4],['verse',10],['chorus',10],['ending',4]],
        [['intro',10],['verse',10],['verse',10],['chorus',10],['ending',10]],
    ],
    hiphop: [
        [['intro',4],['verse',4],['verse',4],['ending',4]],
        [['intro',4],['verse',4],['chorus',4],['ending',4]],        
        [['intro',8],['verse',12],['chorus',8],['verse',12],['pre',4],['chorus',8],['ending',8]],
        [['intro',4],['verse',16],['chorus',16],['bridge',8],['chorus',16],['ending',12]],
        [['intro',8],['verse',8],['chorus',8],['verse',8],['chorus',8],['bridge',8],['ending',8]],
        [['intro',12],['verse',16],['chorus',16],['bridge',12],['ending',12]],
        [['intro',8],['verse',16],['chorus',16],['ending',8]],
        [['intro',4],['verse',16],['bridge',12],['verse',16],['ending',8]],
        [['intro',4],['verse',12],['pre',4],['chorus',12],['verse',12],['ending',4]],
        [['intro',10],['verse',10],['chorus',10],['ending',10]],
        [['intro',4],['verse',10],['bridge',10],['chorus',10],['ending',4]],
    ],
};

// ---------------------------------------------------------------------------
// Valid sequences pool (creative / crazy modes)
// ---------------------------------------------------------------------------

export const VALID_SEQUENCES: SectionName[][] = [
    ['intro','verse','chorus','verse','pre','chorus','bridge','chorus','ending'],
    ['intro','verse','chorus','verse','chorus','bridge','chorus','ending'],
    ['intro','verse','chorus','verse','chorus','bridge','verse','verse','ending'],
    ['intro','verse','chorus','verse','chorus','bridge','verse','chorus','ending'],
    ['intro','verse','chorus','verse','chorus','bridge','chorus','chorus','ending'],
    ['intro','verse','chorus','verse','chorus','chorus','ending'],
    ['intro','verse','chorus','bridge','verse','pre','chorus','ending'],
    ['intro','verse','verse','chorus','bridge','verse','chorus','ending'],
    ['intro','verse','verse','chorus','bridge','verse','chorus','chorus','ending'],
    ['intro','verse','verse','chorus','bridge','verse','verse','chorus','ending'],
    ['intro','verse','verse','pre','chorus','chorus','bridge','chorus','chorus','ending'],
    ['intro','verse','pre','chorus','verse','pre','chorus','bridge','chorus','ending'],
    ['intro','verse','pre','chorus','verse','chorus','bridge','ending'],
    ['intro','verse','pre','chorus','verse','pre','chorus','bridge','ending'],
    ['intro','verse','pre','chorus','verse','bridge','pre','chorus','ending'],
    ['intro','verse','pre','chorus','verse','pre','chorus','bridge','verse','chorus','ending'],
    ['intro','verse','pre','chorus','verse','pre','chorus','bridge','chorus','chorus','ending'],
    ['intro','verse','pre','chorus','verse','chorus','bridge','chorus','ending'],
    ['intro','chorus','verse','pre','chorus','verse','pre','chorus','bridge','ending'],
    ['verse','chorus','verse','chorus','bridge','chorus','ending'],
    ['verse','chorus','verse','chorus','verse','bridge','chorus','ending'],
    ['verse','chorus','verse','chorus','chorus','bridge','chorus','ending'],
    ['verse','chorus','verse','pre','chorus','bridge','chorus','ending'],
    ['verse','chorus','chorus','verse','chorus','bridge','chorus','ending'],
    ['verse','chorus','verse','chorus','bridge','verse','chorus','ending'],
    ['verse','chorus','pre','verse','chorus','bridge','pre','chorus','ending'],
    ['verse','verse','chorus','bridge','chorus','ending'],
    ['verse','verse','chorus','verse','chorus','bridge','verse','ending'],
    ['verse','verse','chorus','verse','bridge','chorus','ending'],
    ['verse','verse','pre','chorus','verse','pre','chorus','bridge','chorus','ending'],
    ['verse','verse','pre','chorus','verse','chorus','bridge','ending'],
    ['verse','verse','chorus','verse','pre','chorus','bridge','ending'],
    ['verse','pre','chorus','verse','chorus','bridge','chorus','ending'],
    ['verse','pre','chorus','chorus','verse','pre','chorus','chorus','bridge','chorus','ending'],
    ['verse','pre','chorus','verse','chorus','verse','chorus','ending'],
];

// ---------------------------------------------------------------------------
// Style sequence variations — computed at module load (mirrors Python logic)
// ---------------------------------------------------------------------------

function _buildStyleSequenceVariations(): Record<string, SectionName[][]> {
    const styles = ['blues','rock','pop','funk','garage','jazz','hiphop','reggae','country','upbeat','brushes','boggie'];
    const result: Record<string, SectionName[][]> = {};
    for (const s of styles) result[s] = [];

    for (const seq of VALID_SEQUENCES) {
        const sset = new Set(seq);
        const verseCount = seq.filter(s => s === 'verse').length;
        const chorusCount = seq.filter(s => s === 'chorus').length;

        // Blues/Boggie: mostly verses and chorus (repetitive groove)
        if (verseCount >= 2 && sset.has('chorus')) {
            result['blues'].push(seq);
            result['boggie'].push(seq);
        }
        // Rock/Pop/Funk/Country: must have verse+chorus+bridge
        if (sset.has('verse') && sset.has('chorus') && sset.has('bridge')) {
            result['rock'].push(seq);
            result['pop'].push(seq);
            result['funk'].push(seq);
            result['country'].push(seq);
        }
        // Garage/Upbeat: short sequences with chorus
        if (seq.length <= 9 && sset.has('chorus')) {
            result['garage'].push(seq);
            result['upbeat'].push(seq);
        }
        // Jazz/Brushes: repeated verses (melodic, head-based)
        if (verseCount >= 2) {
            result['jazz'].push(seq);
            result['brushes'].push(seq);
        }
        // Hiphop: pre or bridge + chorus
        if (sset.has('pre') || sset.has('bridge')) {
            result['hiphop'].push(seq);
        }
        // Reggae: repeated chorus or pre
        if (sset.has('chorus') && (sset.has('pre') || chorusCount >= 2)) {
            result['reggae'].push(seq);
        }
    }
    return result;
}

export const STYLE_SEQUENCES_VARIATIONS: Record<string, SectionName[][]> = _buildStyleSequenceVariations();

// ---------------------------------------------------------------------------
// Style resolution — genres without their own pattern tables
// ---------------------------------------------------------------------------

/**
 * Genres without their own pattern tables borrow from a close family.
 * Placeholder until dedicated EDM/house/techno tables exist. Ticket 20260923a.
 */
export const STYLE_PATTERN_ALIASES: Record<string, string> = {
    edm: 'hiphop', house: 'hiphop', techno: 'hiphop', dance: 'hiphop',
    trap: 'hiphop', synth: 'hiphop', indie: 'rock',
};

/** Fallback family for any genre that is neither a pattern key nor an alias. */
const FALLBACK_PATTERN_STYLE = 'rock';

/**
 * Resolve an album genre to a style key that has pattern tables.
 * Never throws: unknown genres fall back to FALLBACK_PATTERN_STYLE.
 */
export function resolvePatternStyle(style: string): string {
    const s = style.trim().toLowerCase();
    if (s in CANONICAL_STANDARD) return s;
    return STYLE_PATTERN_ALIASES[s] ?? FALLBACK_PATTERN_STYLE;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _randomChoice<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Pick a bar count for a section, respecting validBars if provided.
 *
 * 1. Start with SECTION_RANGES[section]
 * 2. If validBars[section] provided, filter to values present in that list (tolerance 0.05)
 * 3. If nothing remains after filtering, pick from validBars[section] directly
 * 4. If validBars not provided, pick from SECTION_RANGES[section]
 */
function _pickBars(section: SectionName, validBars?: Record<string, number[]>): number {
    const ranges = SECTION_RANGES[section];
    if (!validBars || !validBars[section]) {
        return _randomChoice(ranges);
    }
    const vb = validBars[section];
    const TOLERANCE = 0.05;
    const filtered = ranges.filter(r => vb.some(v => Math.abs(v - r) <= TOLERANCE));
    if (filtered.length > 0) {
        return _randomChoice(filtered);
    }
    // Fallback: pick directly from validBars[section]
    return _randomChoice(vb);
}

// ---------------------------------------------------------------------------
// Main exported function
// ---------------------------------------------------------------------------

/**
 * Generate a song pattern for the given style and mode.
 *
 * @param style      - any genre string; resolved via resolvePatternStyle()
 * @param mode       - 'standard' | 'creative' | 'crazy'
 * @param withIntro  - if true, include intro section; if false, skip it
 * @param withEnding - if true, include ending section; if false, skip it
 * @param validBars  - optional per-section achievable bar counts (floats allowed)
 * @returns          array of {section: bars} objects
 */
export function generateSongPattern(
    style: string,
    mode: Mode = 'standard',
    withIntro: boolean = true,
    withEnding: boolean = true,
    validBars?: Record<string, number[]>,
): SongPattern {
    const pattern: SongPattern = [];
    // The throw branches below are a safety net; resolved styles always have tables.
    style = resolvePatternStyle(style);

    if (mode === 'standard') {
        if (!(style in CANONICAL_STANDARD)) {
            throw new Error(`No canonical sequences defined for style '${style}'`);
        }
        const variant = _randomChoice(CANONICAL_STANDARD[style]);
        for (const [section, bars] of variant) {
            if (section === 'intro' && !withIntro) continue;
            if (section === 'ending' && !withEnding) continue;
            pattern.push({ [section]: bars } as PatternSlot);
        }

    } else if (mode === 'creative') {
        if (!(style in STYLE_SEQUENCES_VARIATIONS) || STYLE_SEQUENCES_VARIATIONS[style].length === 0) {
            throw new Error(`No creative sequences available for style '${style}'`);
        }
        let sequence = _randomChoice(STYLE_SEQUENCES_VARIATIONS[style]);
        if (!withIntro)  sequence = sequence.filter(s => s !== 'intro');
        if (!withEnding) sequence = sequence.filter(s => s !== 'ending');

        // One bar count per unique section type
        const sectionBarMap: Partial<Record<SectionName, number>> = {};
        for (const s of new Set(sequence)) {
            sectionBarMap[s] = _pickBars(s, validBars);
        }
        for (const section of sequence) {
            pattern.push({ [section]: sectionBarMap[section]! } as PatternSlot);
        }

    } else if (mode === 'crazy') {
        if (!(style in STYLE_SEQUENCES_VARIATIONS) || STYLE_SEQUENCES_VARIATIONS[style].length === 0) {
            throw new Error(`No crazy sequences available for style '${style}'`);
        }
        let sequence = _randomChoice(STYLE_SEQUENCES_VARIATIONS[style]);
        if (!withIntro)  sequence = sequence.filter(s => s !== 'intro');
        if (!withEnding) sequence = sequence.filter(s => s !== 'ending');

        // Independent bar count per slot (but consecutive identical sections share a pick per run)
        let i = 0;
        while (i < sequence.length) {
            const section = sequence[i];
            let repeatCount = 1;
            while (i + repeatCount < sequence.length && sequence[i + repeatCount] === section) {
                repeatCount++;
            }
            for (let r = 0; r < repeatCount; r++) {
                pattern.push({ [section]: _pickBars(section, validBars) } as PatternSlot);
            }
            i += repeatCount;
        }

    } else {
        throw new Error(`mode must be 'standard', 'creative', or 'crazy'`);
    }

    return pattern;
}


